/**
 * The base prompt for AI coding agents (what Skapi is, its API, how to build a
 * web app on it), served from the CDN. Every tool reads the same file under
 * its own name, so the project page downloads that one file under each name.
 *
 * Renaming a cross-origin file needs the bytes in hand: a plain
 * `<a download="CLAUDE.md">` to another origin is ignored by browsers. So the
 * file is fetched once (the CDN has to allow it with CORS), kept for the rest
 * of the visit, and handed to the browser as a Blob with the wanted name.
 */
export const AGENT_PROMPT_URL = "https://docs.skapi.com/SKAPI.md";

export interface AgentPlatform {
    /** The key into AGENT_LOGOS. */
    key: string;
    name: string;
    /** The filename the tool reads from the project root. */
    filename: string;
}

export const AGENT_PLATFORMS: AgentPlatform[] = [
    { key: "claude", name: "Claude Code", filename: "CLAUDE.md" },
    { key: "openai", name: "Codex CLI / OpenCode", filename: "AGENTS.md" },
    { key: "gemini", name: "Gemini CLI", filename: "GEMINI.md" },
    { key: "copilot", name: "GitHub Copilot", filename: "copilot-instructions.md" },
    { key: "cursor", name: "Cursor", filename: ".cursorrules" },
    { key: "windsurf", name: "Windsurf", filename: ".windsurfrules" },
];

/** The opening prompt, with the project's id filled in. */
export function agentPrompt(projectId: string): string {
    return `My Skapi project ID is: "${projectId}".\nBuild me a [describe what you want].`;
}

let promptFile: Promise<Blob> | null = null;

/** The prompt's bytes, fetched once per visit. A failed fetch is not kept. */
function fetchAgentPrompt(): Promise<Blob> {
    if (!promptFile) {
        promptFile = fetch(AGENT_PROMPT_URL, { mode: "cors" })
            .then((res) => {
                if (!res.ok) throw new Error(`The prompt could not be fetched (${res.status}).`);
                return res.blob();
            })
            .catch((err) => {
                promptFile = null;
                throw err;
            });
    }
    return promptFile;
}

/**
 * The name a download really gets: browsers drop a leading dot from a
 * suggested filename (Chrome saves ".cursorrules" as "cursorrules"), so the
 * dot-files are asked for without it and the visitor is told to add it back.
 */
export function savedFilename(filename: string): string {
    return filename.replace(/^\./, "");
}

/**
 * Saves the base prompt as `filename` (see savedFilename for the dot-files)
 * and resolves to the name it was saved under. Throws when the file cannot be
 * fetched (no CORS on the CDN, offline); the caller decides what to offer.
 */
export async function downloadAgentPrompt(filename: string): Promise<string> {
    const blob = await fetchAgentPrompt();
    const saved = savedFilename(filename);
    const url = URL.createObjectURL(new Blob([blob], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = saved;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return saved;
}
